import type {DcaaRepository} from '@yandex-delivery/api/repo/dcaa'
import type {SuggestService} from '@yandex-delivery/api/service/suggest'
import {merge, pipe} from '@yandex-delivery/utils'
import type {Order as FormValues} from 'src/shared/types'
import {DeliveryType} from 'src/shared/types'

import {fetchDestinationPoints} from './fetchers/fetch-destination-points'
import {fetchReturnPoint} from './fetchers/fetch-return-point'
import {fetchSourcePoint} from './fetchers/fetch-source-point'
import {toDue} from './mappers/to-due'
import {toPaymentInfo} from './mappers/to-payment-info'
import {toSelectedRequirementsMap} from './mappers/to-selected-requirements-map'

export type PreOrderFormServiceParams = {
  dcaaRepository: DcaaRepository
  suggestService: SuggestService
  config: {
    country: string
  }
}

export function createPreOrderFormService({
  dcaaRepository: {fetchPreOrderRetrieve},
  suggestService,
  config: {country},
}: PreOrderFormServiceParams) {
  return {
    fetchPreOrderForm: async (preOrderId: string) => {
      const preOrderRetrieve = await fetchPreOrderRetrieve({
        pre_order_id: preOrderId,
        country,
      })

      const [sourcePoint, destinationPoints, returnPoint] = await Promise.all([
        fetchSourcePoint(suggestService, preOrderRetrieve),
        fetchDestinationPoints(suggestService, preOrderRetrieve),
        fetchReturnPoint(suggestService, preOrderRetrieve),
      ])

      const prefilledVerticalRequirements =
        toSelectedRequirementsMap(preOrderRetrieve)
      const due = toDue(preOrderRetrieve)

      const paymentInfo = toPaymentInfo(preOrderRetrieve)

      return function _withPreOrderValues(
        defaultValues: FormValues,
      ): FormValues {
        const offerFlow: FormValues['offerFlow'] = {
          vertical: {
            name: 'cargo',
            requirements:
              defaultValues.__settings.__verticals?.['cargo'].requirements,
          },
          ...due,
        }

        Object.entries(prefilledVerticalRequirements).forEach(
          ([name, selectedValue]) => {
            if (offerFlow.vertical?.requirements?.[name]?.selectedValue) {
              offerFlow.vertical.requirements[name].selectedValue =
                selectedValue
            }
          },
        )

        const preOrderValues: Partial<FormValues> = {
          deliveryType: DeliveryType.Cargo,
          sourcePoint,
          destinationPoints,
          returnPoint,
          offerFlow,
          paymentInfo,
          _preOrderId: preOrderId,
          _referralSource: preOrderRetrieve.claim.referral_source,
        }

        if (defaultValues.offerFlow) {
          defaultValues.offerFlow = {}
        }

        const merged = pipe(preOrderValues, merge(defaultValues))

        return merged
      }
    },
  }
}
